import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { CurrencyPicker } from "@/components/CurrencyPicker";
import { storage } from "@/lib/storage";
import { formatMoney } from "@/lib/currencies";
import { razorpayApi } from "@/lib/razorpay";
import { filesApi } from "@/lib/files";
import {
  QrCode, Keyboard, Nfc, Banknote, History, ArrowUpRight, TrendingUp,
  Globe2, Wallet, Crown, Settings, Store, Sparkles,
} from "lucide-react";

export default function MerchantDashboard() {
  const navigate = useNavigate();
  const [currency, setCurrency] = useState(storage.getCurrency());
  const [profile, setProfile] = useState(storage.getProfile());
  const [txs, setTxs] = useState([]);
  const [walletBal, setWalletBal] = useState(0);
  const [sub, setSub] = useState({ active: false });
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    setTxs(storage.getTransactions());
    if (!profile) {
      const p = { name: "Merchant", id: `M${Date.now().toString().slice(-6)}` };
      storage.setProfile(p);
      setProfile(p);
    }
    const mid = (profile || storage.getProfile())?.id;
    if (mid) {
      razorpayApi.wallet(mid).then(({ data }) => setWalletBal(data.balance || 0)).catch(() => {});
      razorpayApi.subscription(mid).then(({ data }) => setSub(data)).catch(() => {});
      filesApi.list({ ownerId: mid, category: "logo" }).then(({ data }) => {
        if (data.files?.[0]) setLogoUrl(filesApi.downloadUrl(data.files[0].id, mid));
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  const stats = useMemo(() => {
    const received = txs.filter((t) => t.direction === "received" && t.currency === currency);
    const total = received.reduce((s, t) => s + Number(t.amount || 0), 0);
    const today = received.filter((t) => {
      const d = new Date(t.createdAt);
      const now = new Date();
      return d.toDateString() === now.toDateString();
    });
    const todayTotal = today.reduce((s, t) => s + Number(t.amount || 0), 0);
    return { total, count: received.length, todayTotal };
  }, [txs, currency]);

  const offlineTiles = [
    { id: "qr", icon: QrCode, label: "QR Code", sub: "Show & receive", path: "/receive/qr", testid: "tile-qr" },
    { id: "manual", icon: Keyboard, label: "Manual", sub: "Enter amount", path: "/receive/manual", testid: "tile-manual" },
    { id: "nfc", icon: Nfc, label: "NFC Tap", sub: "Card reader", path: "/receive/nfc", testid: "tile-nfc" },
    { id: "cash", icon: Banknote, label: "Cash / UPI", sub: "Record payment", path: "/receive/cash", testid: "tile-cash" },
  ];

  const onlineTiles = [
    { id: "online", icon: Globe2, label: "Online Pay", sub: "Razorpay checkout", path: "/online-pay", testid: "tile-online" },
    { id: "wallet", icon: Wallet, label: "Wallet", sub: `₹${walletBal.toFixed(0)} available`, path: "/wallet", testid: "tile-wallet" },
    { id: "plans", icon: Crown, label: "Plans", sub: sub.active ? `${sub.subscription?.planId}` : "Upgrade", path: "/plans", testid: "tile-plans" },
    { id: "settings", icon: Settings, label: "Settings", sub: "Logo & files", path: "/settings", testid: "tile-settings" },
  ];

  return (
    <div className="page-shell" data-testid="merchant-dashboard">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="w-12 h-12 rounded-xl object-cover border gold-border" data-testid="dash-logo" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-[rgba(230,180,85,0.1)] flex items-center justify-center">
              <Store size={22} className="text-[#E6B455]" />
            </div>
          )}
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-[#F7E6B4]" data-testid="dash-name">
              Welcome, {profile?.name || "Merchant"}
            </h1>
            <div className="text-xs text-[#F7E6B4]/55 font-mono">{profile?.id}</div>
          </div>
        </div>
        <CurrencyPicker value={currency} onChange={setCurrency} compact />
      </div>

      <div className="card-glass gold-glow rounded-2xl p-6 mb-6 fade-slide-up relative overflow-hidden" data-testid="balance-hero">
        <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[rgba(230,180,85,0.08)] blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={14} className="text-[#E6B455]" />
            <span className="text-[11px] uppercase tracking-[0.14em] text-[#F7E6B4]/60 font-medium">
              Total received
            </span>
          </div>
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div className="text-4xl md:text-5xl font-bold gold-text" data-testid="total-received">
              {formatMoney(stats.total, currency)}
            </div>
            <button
              onClick={() => navigate("/history")}
              className="btn-outline-gold px-3.5 py-2 rounded-full text-xs flex items-center gap-1.5"
              data-testid="view-history-btn"
            >
              History <ArrowUpRight size={12} />
            </button>
          </div>
          <div className="mt-4 pt-4 border-t border-[rgba(230,180,85,0.14)] flex gap-6">
            <div>
              <div className="stat-label">Today</div>
              <div className="text-lg font-semibold text-[#F7E6B4]" data-testid="today-total">
                {formatMoney(stats.todayTotal, currency)}
              </div>
            </div>
            <div>
              <div className="stat-label">Transactions</div>
              <div className="text-lg font-semibold text-[#F7E6B4]" data-testid="tx-count">
                {stats.count}
              </div>
            </div>
            <div>
              <div className="stat-label">Wallet</div>
              <div className="text-lg font-semibold text-[#F7E6B4]" data-testid="wallet-mini">
                ₹{walletBal.toFixed(0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <h2 className="text-base md:text-lg font-semibold text-[#F7E6B4] mb-3">
        Offline methods
      </h2>
      <div className="bento-grid mb-6 fade-slide-up" style={{ animationDelay: "0.08s" }}>
        {offlineTiles.map((t) => (
          <button
            key={t.id}
            onClick={() => navigate(t.path)}
            className="bento-tile text-left"
            data-testid={t.testid}
          >
            <div className="bento-icon">
              <t.icon size={22} />
            </div>
            <div className="font-semibold text-[#F7E6B4] text-sm">{t.label}</div>
            <div className="text-[11px] text-[#F7E6B4]/55 mt-0.5">{t.sub}</div>
          </button>
        ))}
      </div>

      <h2 className="text-base md:text-lg font-semibold text-[#F7E6B4] mb-3 flex items-center gap-2">
        Online & more
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(230,180,85,0.1)] text-[#E6B455] uppercase tracking-wider">
          Needs internet
        </span>
      </h2>
      <div className="bento-grid mb-8 fade-slide-up" style={{ animationDelay: "0.12s" }}>
        {onlineTiles.map((t) => (
          <button
            key={t.id}
            onClick={() => navigate(t.path)}
            className="bento-tile text-left"
            data-testid={t.testid}
          >
            <div className="bento-icon">
              <t.icon size={22} />
            </div>
            <div className="font-semibold text-[#F7E6B4] text-sm">{t.label}</div>
            <div className="text-[11px] text-[#F7E6B4]/55 mt-0.5 truncate">{t.sub}</div>
          </button>
        ))}
      </div>

      <button
        onClick={() => navigate("/assistant")}
        className="w-full card-glass gold-border rounded-2xl p-5 mb-8 flex items-center gap-4 text-left hover:border-[rgba(230,180,85,0.45)] transition group fade-slide-up"
        data-testid="ai-assistant-card"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E6B455] to-[#B8902F] flex items-center justify-center">
          <Sparkles size={22} className="text-[#0A0F1E]" />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-[#F7E6B4]">AI Assistant</div>
          <div className="text-xs text-[#F7E6B4]/55 mt-0.5">"Aaj kitna paisa aaya?" — poochhiye, turant jawab</div>
        </div>
        <ArrowUpRight size={18} className="text-[#E6B455] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </button>

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base md:text-lg font-semibold text-[#F7E6B4]">Recent activity</h2>
        <button
          onClick={() => navigate("/history")}
          className="text-xs text-[#E6B455] hover:underline flex items-center gap-1"
          data-testid="all-activity-link"
        >
          See all <History size={12} />
        </button>
      </div>
      <div className="space-y-2" data-testid="recent-activity">
        {txs.slice(0, 4).map((t) => (
          <div
            key={t.id}
            className="card-glass rounded-xl px-4 py-3 flex items-center justify-between"
            data-testid={`recent-tx-${t.id}`}
          >
            <div>
              <div className="text-sm font-semibold text-[#F7E6B4] capitalize">
                {t.method} · {t.direction}
              </div>
              <div className="text-[11px] text-[#F7E6B4]/55">
                {new Date(t.createdAt).toLocaleString()}
              </div>
            </div>
            <div
              className={`text-base font-bold ${
                t.direction === "received" ? "text-[#E6B455]" : "text-[#F7E6B4]/80"
              }`}
            >
              {t.direction === "received" ? "+" : "-"} {formatMoney(t.amount, t.currency)}
            </div>
          </div>
        ))}
        {txs.length === 0 && (
          <div className="card-glass rounded-xl p-8 text-center" data-testid="empty-activity">
            <History size={28} className="text-[#F7E6B4]/40 mx-auto mb-2" />
            <p className="text-sm text-[#F7E6B4]/60">
              No transactions yet. Start by picking a method above.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
